import { DOCUMENT, ViewportScroller } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';
import { ToastModule } from 'primeng/toast';
import { FooterComponent } from "./shared/layouts/footer/footer.component";
import { HeaderComponent } from "./shared/layouts/header/header.component";

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, HeaderComponent, FooterComponent, ToastModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css'
})
export class AppComponent {
  title = 'Booking-Appointment-Landing-Page';

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly document = inject(DOCUMENT);
  private readonly viewportScroller = inject(ViewportScroller);

  /**
   * Whether to render the site header and footer. A route opts out with
   * `data: { chrome: false }` — full-bleed pages such as login own the whole
   * viewport. Anything that does not say otherwise keeps the chrome.
   */
  readonly showChrome = signal(true);

  /** Set once the router has rendered its first page; gates the footer. */
  readonly pageReady = signal(false);

  constructor() {
    // The router scrolls to a fragment with a plain window.scrollTo, which
    // ignores scroll-margin-top — so the section would land under the fixed
    // header. This offset is what it subtracts instead. Measured on each
    // scroll rather than hard-coded, because the header's height changes
    // with the viewport width.
    this.viewportScroller.setOffset(() => {
      const header = this.document.querySelector('.site-header');
      return [0, header ? Math.ceil(header.getBoundingClientRect().height) : 0];
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        // Any route on the active chain can opt out — the dashboard shell
        // sets it once on the parent and every child page inherits it.
        let chrome = true;
        let current: ActivatedRoute | null = this.route;
        while (current) {
          if (current.snapshot.data['chrome'] === false) {
            chrome = false;
          }
          current = current.firstChild;
        }
        this.showChrome.set(chrome);
      });
  }
}
